// import { Page, Text, View, Document, StyleSheet, Font, Image as PdfImage } from '@react-pdf/renderer'; // Comment out
// import { saveAs } from 'file-saver'; // Keep if used by other exports
// import html2canvas from 'html2canvas'; // Keep if used by other exports
import { Recipe } from '@/types/recipe';
import JSZip from 'jszip';
import { saveAs } from 'file-saver'; // Ensure this is imported if exportMultipleToMarkdown uses it

// ... any existing font registration or styles for PDF, comment them out ...
// Font.register({ 
//   family: 'Roboto',
//   fonts: [
//     { src: '/fonts/Roboto-Regular.ttf' },
//     { src: '/fonts/Roboto-Bold.ttf', fontWeight: 'bold' },
//   ]
// });

// const pdfStyles = StyleSheet.create({
//   page: { fontFamily: 'Roboto', padding: 30, fontSize: 10 },
//   title: { fontSize: 18, marginBottom: 10, fontWeight: 'bold', textAlign: 'center' },
//   sectionTitle: { fontSize: 14, marginTop: 15, marginBottom: 5, fontWeight: 'bold' },
//   text: { marginBottom: 5, lineHeight: 1.4 },
//   image: { width: '80%', maxHeight: 200, objectFit: 'contain', alignSelf: 'center', marginVertical: 10 },
// });

// export const exportToPDF = async (recipe: Recipe) => {
//   // Comment out the entire function body or the function itself
//   console.warn("PDF export functionality is temporarily disabled.");
//   return; 
//   /*
//   const MyDocument = (
//     <Document>
//       <Page style={pdfStyles.page}>
//         <Text style={pdfStyles.title}>{recipe.title}</Text>
//         {recipe.image && <PdfImage style={pdfStyles.image} src={recipe.image} />}
//         <Text style={pdfStyles.text}>{recipe.description}</Text>
        
//         <Text style={pdfStyles.sectionTitle}>Ingredients</Text>
//         <Text style={pdfStyles.text}>{recipe.ingredients}</Text>
        
//         <Text style={pdfStyles.sectionTitle}>Method</Text>
//         <Text style={pdfStyles.text}>{recipe.method}</Text>
        
//         {recipe.notes && (
//           <>
//             <Text style={pdfStyles.sectionTitle}>Notes</Text>
//             <Text style={pdfStyles.text}>{recipe.notes}</Text>
//           </>
//         )}
//       </Page>
//     </Document>
//   );

//   const { pdf } = await import('@react-pdf/renderer'); // Dynamic import might also be a source of issues if not handled carefully
//   const blob = await pdf(MyDocument).toBlob();
//   saveAs(blob, `${recipe.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
//   */
// };

// Keep other export functions like exportToMarkdown if they exist and are used
export const exportToMarkdown = (recipe: Recipe): string => {
  let markdown = `# ${recipe.title}\n\n`;
  if (recipe.description) {
    markdown += `${recipe.description}\n\n`;
  }
  if (recipe.image_url) {
    markdown += `![${recipe.title}](${recipe.image_url})\n\n`;
  } else if (recipe.image) {
    markdown += `![${recipe.title}](${recipe.image})\n\n`;
  }
  markdown += `## Ingredients\n${recipe.ingredients}\n\n`;
  markdown += `## Method\n${recipe.method}\n\n`;
  if (recipe.notes) {
    markdown += `## Notes\n${recipe.notes}\n\n`;
  }
  markdown += `---\n`;
  markdown += `**Metadata**\n`;
  markdown += `- ID: ${recipe.id}\n`;
  markdown += `- Category: ${recipe.category || 'N/A'}\n`;
  markdown += `- Tags: ${(recipe.tags || []).join(', ')}\n`;
  markdown += `- Date Created: ${recipe.dateCreated || 'N/A'}\n`;
  markdown += `- Date Modified: ${recipe.dateModified || 'N/A'}\n`;
  markdown += `- Favorite: ${recipe.isFavorite ? 'Yes' : 'No'}\n`;
  markdown += `- Has Custom Image: ${recipe.has_custom_image ? 'Yes' : 'No'}\n`;
  markdown += `- Shared: ${recipe.share ? 'Yes' : 'No'}\n`;
  return markdown;
};

export const exportMultipleToMarkdown = async (recipes: Recipe[]) => {
  if (recipes.length === 0) return;

  if (recipes.length === 1) {
    const markdown = exportToMarkdown(recipes[0]);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, `${recipes[0].title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`);
  } else {
    const zip = new JSZip();
    recipes.forEach(recipe => {
      const markdown = exportToMarkdown(recipe);
      zip.file(`${recipe.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`, markdown);
    });
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, 'recipes_export.zip');
  }
};


// JSON export function
export const exportMultipleToJson = async (recipes: Recipe[]) => {
  if (recipes.length === 0) return;

  const json = JSON.stringify(recipes, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  saveAs(blob, 'recipes_export.json');
};

// CSV export function
export const exportMultipleToCsv = async (recipes: Recipe[]) => {
  if (recipes.length === 0) return;

  const csvRows = [];

  // Header row
  const headers = ['id', 'title', 'description', 'category', 'tags', 'ingredients', 'method', 'notes', 'image_url', 'has_custom_image', 'dateCreated', 'dateModified', 'isFavorite', 'share'];
  csvRows.push(headers.join(','));

  // Data rows
  recipes.forEach(recipe => {
    const row = headers.map(field => {
      let value = recipe[field as keyof Recipe];
      if (value === undefined || value === null) value = '';
      if (Array.isArray(value)) value = value.join(';');
      if (typeof value === 'string') value = value.replace(/"/g, '""');
      if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
      return `"${value}"`;
    });
    csvRows.push(row.join(','));
  });

  const csv = csvRows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, 'recipes_export.csv');
};

// If there are other functions that call exportToPDF, you'll need to comment out those calls too.
// For example, in a component:
// const handlePdfExport = () => {
//   // exportToPDF(currentRecipe); // Comment this out
//   alert("PDF Export is temporarily disabled.");
// }
